import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { UserCardResponse } from '../api/types';
import logo from '../assets/whr-logo.png';
import './UserCardPage.css';

type CardState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; card: UserCardResponse };

function UserCardPage() {
  const { userId, method } = useParams<{ userId: string; method?: string }>();
  const [state, setState] = useState<CardState>({ kind: 'loading' });
  const [copyStatus, setCopyStatus] = useState<string>('');
  const [activeMethod, setActiveMethod] = useState<'usdt' | 'card'>(
    (method === 'card' ? 'card' : 'usdt')
  );
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = async () => {
    const doc = document as any;
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement
    );

    if (!isCurrentlyFullscreen) {
      try {
        const elem = document.documentElement as any;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        } else if (elem.mozRequestFullScreen) {
          await elem.mozRequestFullScreen();
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
        setIsFullscreen(true);
      } catch {
        // Fullscreen not supported
      }
    } else {
      try {
        const doc = document as any;
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
        setIsFullscreen(false);
      } catch {
        // Error exiting fullscreen
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = document as any;
      const isFullscreen = !!(
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );
      setIsFullscreen(isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function fetchCard() {
      if (!userId) {
        setState({ kind: 'error', message: 'Card not found.' });
        return;
      }

      try {
        const card = await api.getPublicCard(userId);
        if (mounted) {
          setState({ kind: 'loaded', card });
          if (method === 'card' && card.paymentMethods?.card?.available) {
            setActiveMethod('card');
          }
        }
      } catch (error) {
        if (mounted) {
          setState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Card unavailable.',
          });
        }
      }
    }

    void fetchCard();

    return () => {
      mounted = false;
    };
  }, [userId, method]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 1800);
    } catch {
      setCopyStatus('Failed to copy');
      setTimeout(() => setCopyStatus(''), 1800);
    }
  };

  if (state.kind === 'loading') {
    return (
      <div className="user-card-page">
        <div className="user-card-container">
          <div className="user-card-loading">Loading card...</div>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="user-card-page">
        <div className="user-card-container">
          <div className="user-card-error">
            <h2>Card unavailable</h2>
            <p>{state.message}</p>
          </div>
        </div>
      </div>
    );
  }

  const { card } = state;
  const displayName = card.displayName || (card.user.username ? `@${card.user.username}` : 'User');
  const currentPayment = card.paymentMethods?.[activeMethod];
  const isBankCardUrl = card.bankCard?.startsWith('http');

  return (
    <div className={`user-card-page ${isFullscreen ? 'fullscreen-active' : ''}`}>
      <div className="user-card-container">
        {/* Header */}
        <header className="user-card-header">
          <div className="user-card-header-left">
            <div className="user-card-avatar">
              {card.photoUrl ? (
                <img src={card.photoUrl} alt={displayName} />
              ) : (
                <img src={logo} alt="Plz Pay Me" className="user-card-avatar-logo" />
              )}
            </div>
            <div className="user-card-name-block">
              <h1 className="user-card-title">{displayName}</h1>
            </div>
          </div>

          <div className="user-card-header-actions">
            {/* Show tabs only if both payment methods are available */}
            {card.paymentMethods?.card?.available && (
              <div className="user-card-tabs">
                <button
                  type="button"
                  className={`user-card-tab ${activeMethod === 'usdt' ? 'active' : ''}`}
                  onClick={() => setActiveMethod('usdt')}
                >
                  USDT
                </button>
                <button
                  type="button"
                  className={`user-card-tab ${activeMethod === 'card' ? 'active' : ''}`}
                  onClick={() => setActiveMethod('card')}
                >
                  Card
                </button>
              </div>
            )}
            <button
              type="button"
              className="user-card-fullscreen-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              )}
            </button>
          </div>
        </header>

        {/* Content Grid - 2 columns: description + QR, then address on full width */}
        <div className={`user-card-content-grid ${!card.description ? 'centered-qr' : ''}`}>
          {/* Description Card - only show if description exists */}
          {card.description && (
            <div className="user-card-description-card">
              <div className="user-card-description">
                {card.description.split('\n').map((line, i) => (
                  <p key={i}>{line || '\u00A0'}</p>
                ))}
              </div>
            </div>
          )}

          {/* QR Code Only */}
          <div className="user-card-payment-card">
            {activeMethod === 'usdt' ? (
              currentPayment?.available ? (
                currentPayment.qrCode && (
                  <div className="user-card-qr">
                    <img src={currentPayment.qrCode} alt="USDT Payment QR" />
                  </div>
                )
              ) : (
                <div className="user-card-unavailable">
                  <p>USDT payments are not configured yet.</p>
                </div>
              )
            ) : (
              currentPayment?.available ? (
                currentPayment.qrCode && (
                  <div className="user-card-qr">
                    <img src={currentPayment.qrCode} alt="Card Payment QR" />
                  </div>
                )
              ) : (
                <div className="user-card-unavailable">
                  <p>Card payments are not configured yet.</p>
                </div>
              )
            )}
          </div>

          {/* Address Block - Full Width */}
          {activeMethod === 'usdt' && card.wallet && (
            <div className="user-card-address-block">
              <h3 className="user-card-address-label">USDT (TRC-20) Address</h3>
              <div className="user-card-address-row">
                <span className="user-card-address-text">{card.wallet.address}</span>
                <button
                  type="button"
                  className="user-card-copy-btn"
                  onClick={() => copyToClipboard(card.wallet!.address)}
                >
                  Copy
                </button>
              </div>
              {copyStatus && <p className="user-card-copy-status">{copyStatus}</p>}
            </div>
          )}

          {activeMethod === 'card' && card.bankCard && (
            <div className="user-card-address-block">
              <h3 className="user-card-address-label">
                {isBankCardUrl ? 'Payment Link' : 'Bank Card Number'}
              </h3>
              <div className="user-card-address-row">
                {isBankCardUrl ? (
                  <a
                    href={card.bankCard}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="user-card-payment-link"
                  >
                    {card.bankCard}
                  </a>
                ) : (
                  <>
                    <span className="user-card-address-text">{card.bankCard}</span>
                    <button
                      type="button"
                      className="user-card-copy-btn"
                      onClick={() => copyToClipboard(card.bankCard!)}
                    >
                      Copy
                    </button>
                  </>
                )}
              </div>
              {copyStatus && <p className="user-card-copy-status">{copyStatus}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserCardPage;
